import { useState } from 'react';
import { useAppStore } from '../store/useAppStore';
import { createNewSave } from '../utils/storage';
import { Plus, Trash2, FilePlus2, ChevronRight, Loader2 } from 'lucide-react';

export const SaveGameSelector = () => {
  const saves = useAppStore(s => s.saves);
  const currentSaveId = useAppStore(s => s.currentSaveId);
  const setCurrentSave = useAppStore(s => s.setCurrentSave);
  const addSave = useAppStore(s => s.addSave);
  const deleteSave = useAppStore(s => s.deleteSave);
  const user = useAppStore(s => s.user);
  const [isCreating, setIsCreating] = useState(false);
  const [newSaveName, setNewSaveName] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleCreateSave = async () => {
    if (!newSaveName.trim()) return;
    
    setIsLoading(true);
    setError(null);
    
    try {
      console.log('Creating new save:', newSaveName);
      const newSave = createNewSave(newSaveName);
      await addSave(newSave);
      setCurrentSave(newSave.id);
      setNewSaveName('');
      setIsCreating(false);
      console.log('Save created successfully:', newSaveName);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to create save';
      console.error('Failed to create save:', {
        saveName: newSaveName,
        error: err,
      });
      setError(`Failed to create save: ${errorMessage}`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteSave = async (saveId: number, e: React.MouseEvent) => {
    e.stopPropagation();
    if (confirm('Are you sure you want to delete this save?')) {
      setIsLoading(true);
      setError(null);
      
      try {
        console.log('Deleting save:', saveId);
        await deleteSave(saveId);
        console.log('Save deleted successfully:', saveId);
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to delete save';
        console.error('Failed to delete save:', {
          saveId,
          error: err,
        });
        setError(`Failed to delete save: ${errorMessage}`);
      } finally {
        setIsLoading(false);
      }
    }
  };

  if (!user) {
    return (
      <div className="bg-white rounded-lg shadow-md overflow-hidden border border-gray-200">
        <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-6 py-4">
          <h2 className="text-xl font-bold text-white">Your Save Files</h2>
        </div>
        <div className="p-8 text-center text-gray-500">
          <p>Please sign in to manage your save files</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-md overflow-hidden border border-gray-200">
      <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-6 py-4">
        <div className="flex justify-between items-center">
          <h2 className="text-xl font-bold text-white">Your Save Files</h2>
          <span className="bg-white/20 text-white text-sm px-3 py-1 rounded-full font-medium">
            {saves.length} {saves.length === 1 ? 'Save' : 'Saves'}
          </span>
        </div>
      </div>
      
      {error && (
        <div className="bg-red-50 border-l-4 border-red-400 p-4">
          <div className="flex">
            <div className="ml-3">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          </div>
        </div>
      )}
      
      <div className="p-4 space-y-3 max-h-[70vh] overflow-y-auto">
        {saves.length === 0 && !isCreating ? (
          <div className="text-center py-8 text-gray-500">
            <FilePlus2 size={40} className="mx-auto mb-3 text-gray-300" />
            <p className="text-sm text-gray-600">No save games found</p>
            <p className="text-xs text-gray-400 mt-1">Create your first save to get started</p>
          </div>
        ) : (
          saves.map((save) => (
            <div
              key={save.id}
              onClick={() => setCurrentSave(save.id)}
              className={`group relative p-4 rounded-lg cursor-pointer transition-all duration-200 border-2 ${
                currentSaveId === save.id
                  ? 'border-blue-500 bg-blue-50 ring-2 ring-blue-100'
                  : 'border-gray-200 hover:border-blue-300 hover:bg-blue-50/50 hover:shadow-sm'
              }`}
            >
              <div className="flex justify-between items-center">
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-gray-900 truncate">{save.name}</h3>
                  <p className="text-xs text-gray-500 mt-0.5">
                    Created: {new Date(save.createdAt).toLocaleDateString(undefined, {
                      year: 'numeric',
                      month: 'short',
                      day: 'numeric',
                    })}
                  </p>
                </div>
                <div className="flex items-center space-x-2 ml-3">
                  <button
                    onClick={(e) => handleDeleteSave(save.id, e)}
                    disabled={isLoading}
                    className="p-1.5 text-gray-400 hover:text-red-500 transition-colors rounded-full hover:bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    aria-label={`Delete ${save.name}`}
                    title="Delete save"
                  >
                    {isLoading ? <Loader2 size={16} className="animate-spin" /> : <Trash2 size={16} />}
                  </button>
                  <ChevronRight 
                    size={18} 
                    className={`text-gray-400 transition-transform duration-200 ${
                      currentSaveId === save.id ? 'text-blue-500' : 'group-hover:translate-x-1'
                    }`} 
                  />
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {isCreating ? (
        <div className="p-3 bg-gray-50 rounded-lg">
          <input
            type="text"
            value={newSaveName}
            onChange={(e) => setNewSaveName(e.target.value)}
            placeholder="Enter save name"
            className="w-full px-3 py-2 text-sm rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-black"
            autoFocus
            onKeyDown={(e) => e.key === 'Enter' && !isLoading && handleCreateSave()}
            disabled={isLoading}
          />
          <div className="flex justify-end space-x-2 mt-3">
            <button
              onClick={() => setIsCreating(false)}
              disabled={isLoading}
              className="px-3 py-1 text-sm text-gray-600 hover:bg-gray-100 rounded-md transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={handleCreateSave}
              disabled={!newSaveName.trim() || isLoading}
              className="px-3 py-1 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center space-x-2"
            >
              {isLoading && <Loader2 size={14} className="animate-spin" />}
              <span>Save</span>
            </button>
          </div>
        </div>
      ) : (
        <button
          onClick={() => setIsCreating(true)}
          disabled={isLoading}
          className="mt-3 w-full py-2 rounded-lg text-gray-500 hover:border-blue-300 hover:text-blue-600 hover:bg-blue-50 transition-colors flex items-center justify-center space-x-2 text-sm font-medium disabled:opacity-50"
        >
          <Plus size={16} />
          <span>New Save</span>
        </button>
      )}
    </div>
  );
};
